import React, { useState, useContext } from 'react'
import { TableLoader } from 'uiComponents/loaders'
import { InlineDataTable, HeaderRow, TableHeader, Cell, ExpandCell, DynamicRow } from 'uiComponents/table'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { NestedContainerWrapper } from 'uiComponents/table/nestedContainer'
import DiscountRulesTableActions from './actions'
import InlineName from './inlineName'
import StatusCell from './status'
import RuleDefinition from '../ruleDefinition'
import { DiscountRulesContext } from 'discounts/rules/table/tableContext'
import { MetadataContext } from 'discounts/rules/metadataContext'
import { DiscountRule } from 'discounts/rules/discountRulesService'
import { DateFormats, formatISOString } from 'utils/dates'
import { useTranslation } from 'translations'

function DiscountRulesList() {
    const { t } = useTranslation()
    const { rulesList, updating, sort, onSortChanged } = useContext(DiscountRulesContext)
    const { navigation } = useContext(MetadataContext)
    const [currentExpanded, setCurrentExpanded] = useState<number | null>(null)

    const toggleExpanded = (id: number) => {
        const expanded = currentExpanded === id
        expanded ? setCurrentExpanded(null) : setCurrentExpanded(id)
        navigation.addQueryWithReplace({
            discountRule: expanded ? null : id.toString(),
        })
    }

    const formatValidityCell = (rule: DiscountRule) => {
        if (!!rule.validFrom && !!rule.validTo) {
            return `${formatISOString(rule.validFrom, DateFormats.LONG_DATE)} - ${formatISOString(
                rule.validTo,
                DateFormats.LONG_DATE,
            )}`
        }
        if (!rule.validFrom && !!rule.validTo) {
            return t('products.discountRules.table.validity.until', {
                date: formatISOString(rule.validTo, DateFormats.LONG_DATE),
            })
        }
        if (!!rule.validFrom && !rule.validTo) {
            return t('products.discountRules.table.validity.from', {
                date: formatISOString(rule.validFrom, DateFormats.LONG_DATE),
            })
        }
        return t('products.discountRules.table.validity.unlimited')
    }

    const columnWidths = ['4em', '20em', null, '27em', '11em']

    return (
        <InlineDataTable columnWidths={columnWidths} bordered style={{ position: 'relative' }}>
            <HeaderRow>
                <TableHeader nonInteractive noPadding />
                <TableHeader sortKey="enabled" sorting={sort} changeSort={onSortChanged}>
                    {t('products.discountRules.table.columns.status')}
                </TableHeader>
                <TableHeader sortKey="name" sorting={sort} changeSort={onSortChanged}>
                    {t('products.discountRules.table.columns.name')}
                </TableHeader>
                <TableHeader sortKey="validity" sorting={sort} changeSort={onSortChanged}>
                    {t('products.discountRules.table.columns.validity')}
                </TableHeader>
                <TableHeader nonInteractive />
            </HeaderRow>
            {updating && <TableLoader />}
            {!updating && (
                <>
                    {!updating && rulesList.length === 0 && (
                        <NoResultsRow text={t('products.discountRules.table.noResults')} />
                    )}
                    {rulesList.map((rule) => {
                        const expanded = rule.id === currentExpanded
                        return (
                            <div key={rule.id}>
                                <DynamicRow
                                    interactive
                                    highlighted={expanded}
                                    onClick={() => toggleExpanded(rule.id)}
                                    className={expanded ? 'expanded' : ''}
                                >
                                    <ExpandCell className="expand" expanded={expanded} />
                                    <Cell>
                                        <StatusCell rule={rule} />
                                    </Cell>
                                    <Cell title={rule.name}>
                                        <InlineName rule={rule} />
                                    </Cell>
                                    <Cell>{formatValidityCell(rule)}</Cell>
                                    <Cell align="right" onClick={(e) => e.stopPropagation()}>
                                        <DiscountRulesTableActions rule={rule} />
                                    </Cell>
                                </DynamicRow>
                                {expanded && (
                                    <NestedContainerWrapper grade={1} className="expanded">
                                        <RuleDefinition rule={rule} />
                                    </NestedContainerWrapper>
                                )}
                            </div>
                        )
                    })}
                </>
            )}
        </InlineDataTable>
    )
}

export default DiscountRulesList
